import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, X, Send, Loader2, RefreshCw, Zap, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const SUGGESTED = {
  es: [
    "¿Qué está en riesgo esta semana?",
    "¿Qué actividades están bloqueadas?",
    "¿Qué persona necesita atención?",
    "¿Qué cambió desde la última actualización?",
    "¿Qué debo hacer hoy para evitar un retraso?",
    "¿Qué pasará si movemos esta fecha?",
  ],
  en: [
    "What is at risk this week?",
    "Which activities are blocked?",
    "Who needs attention?",
    "What changed since the last update?",
    "What should I do today to avoid delays?",
    "What if we move this date?",
  ],
};

export default function NadIAPanel({ boardId, onClose }) {
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const [draft, setDraft] = useState("");
  const [deep, setDeep] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  const { data: session } = useQuery({
    queryKey: ["nadia-session", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/nadia/session`)).data,
  });
  const { data: usage } = useQuery({
    queryKey: ["nadia-usage", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/nadia/usage`)).data,
    refetchInterval: 30_000,
  });

  const resetMutation = useMutation({
    mutationFn: async () => (await api.post(`/boards/${boardId}/nadia/reset`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nadia-session", boardId] });
      toast.success(lang === "es" ? "Conversación reiniciada" : "Conversation cleared");
    },
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [session?.messages?.length, streamingText]);

  async function sendMessage(text) {
    if (!text.trim() || streaming) return;
    setStreaming(true);
    setStreamingText("");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const token = (document.cookie.match(/session_token=([^;]+)/) || [])[1];
    try {
      const res = await fetch(`${backend}/api/boards/${boardId}/nadia/messages`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text, deep, language: lang }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      qc.invalidateQueries({ queryKey: ["nadia-session", boardId] });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === "delta") setStreamingText(t => t + ev.content);
            else if (ev.type === "error") toast.error(ev.message || "Error");
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setStreaming(false);
      setStreamingText("");
      qc.invalidateQueries({ queryKey: ["nadia-session", boardId] });
      qc.invalidateQueries({ queryKey: ["nadia-usage", boardId] });
      setDraft("");
    }
  }

  const messages = session?.messages || [];
  const outOfQuota = usage && usage.remaining <= 0;

  return (
    <>
      {/* Futuristic overlay */}
      <div
        className="fixed inset-0 z-40 backdrop-blur-xl transition-opacity"
        style={{
          background: "radial-gradient(circle at 30% 20%, rgba(59,130,246,0.25), transparent 55%), radial-gradient(circle at 80% 80%, rgba(139,92,246,0.25), transparent 55%), rgba(4,7,20,0.72)",
        }}
        onClick={onClose}
        data-testid="nadia-overlay"
      />

      {/* Panel */}
      <aside
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[520px] flex flex-col shadow-2xl border-l border-blue-400/20"
        style={{
          background: "linear-gradient(180deg, rgba(11,15,35,0.95), rgba(20,10,45,0.95))",
          backdropFilter: "blur(20px)",
          color: "#e2e8f0",
        }}
        data-testid="nadia-panel"
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full grid place-items-center relative"
               style={{ background: "conic-gradient(from 210deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)" }}>
            <div className="h-8 w-8 rounded-full bg-slate-950 grid place-items-center">
              <Sparkles className="h-4 w-4 text-blue-300" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-semibold text-lg tracking-tight">NadIA</h2>
            <p className="text-[11px] text-blue-200/70">
              {lang === "es" ? "Asistente de Proyecto · IA" : "Project Assistant · AI"}
              {usage && (
                <span className="ml-2 opacity-80" data-testid="nadia-usage">
                  · {usage.used_today}/{usage.limit} {lang === "es" ? "hoy" : "today"}
                </span>
              )}
            </p>
          </div>
          <button onClick={() => resetMutation.mutate()} title={lang === "es" ? "Nueva conversación" : "New chat"}
                  className="p-2 rounded-lg hover:bg-white/10 text-blue-200" data-testid="nadia-reset">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-blue-200" data-testid="nadia-close">
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="nadia-messages">
          {messages.length === 0 && !streaming && (
            <div className="text-center pt-4">
              <div className="mx-auto h-16 w-16 rounded-full grid place-items-center animate-pulse"
                   style={{ background: "conic-gradient(from 210deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)" }}>
                <div className="h-14 w-14 rounded-full bg-slate-950 grid place-items-center">
                  <Sparkles className="h-6 w-6 text-blue-300" />
                </div>
              </div>
              <h3 className="font-display text-xl font-semibold mt-4">
                {lang === "es" ? "Hola, soy NadIA" : "Hi, I'm NadIA"}
              </h3>
              <p className="text-sm text-blue-200/70 mt-1">
                {lang === "es"
                  ? "Analizo tu tablero y te doy respuestas concretas."
                  : "I analyze your board and give you concrete answers."}
              </p>
              <div className="mt-6 space-y-2">
                {SUGGESTED[lang].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setDraft(q); sendMessage(q); }}
                    disabled={outOfQuota}
                    className="w-full text-left px-4 py-3 rounded-xl border border-blue-400/20 bg-white/[0.03] hover:bg-white/[0.08] hover:border-blue-400/40 transition text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`nadia-suggested-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <MessageBubble key={m.message_id} msg={m} />
          ))}
          {streaming && streamingText && (
            <MessageBubble msg={{ role: "assistant", text: streamingText, deep }} live />
          )}
          {streaming && !streamingText && (
            <div className="flex items-center gap-2 text-blue-200/70 text-sm" data-testid="nadia-thinking">
              <Loader2 className="h-4 w-4 animate-spin" />
              {lang === "es" ? "Analizando tu tablero…" : "Analyzing your board…"}
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Quota banner */}
        {outOfQuota && (
          <div className="mx-5 mb-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-2.5 text-xs flex items-start gap-2" data-testid="nadia-out-of-quota">
            <ShieldAlert className="h-4 w-4 mt-0.5 text-amber-400 flex-none" />
            <span className="text-amber-100">
              {lang === "es"
                ? `Alcanzaste el límite diario (${usage.limit} preguntas). Se reinicia mañana.`
                : `You hit your daily limit (${usage.limit} questions). Resets tomorrow.`}
            </span>
          </div>
        )}

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(draft); }}
          className="p-4 border-t border-white/10"
          data-testid="nadia-composer"
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => usage?.deep_available ? setDeep(d => !d) : toast.error(lang === "es" ? "Solo Pro y Corporativo" : "Pro & Corporate only")}
              disabled={!usage?.deep_available}
              className={cn(
                "inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full transition",
                deep && usage?.deep_available
                  ? "bg-gradient-to-r from-violet-500 to-blue-500 text-white shadow"
                  : "bg-white/5 text-blue-200 hover:bg-white/10",
                !usage?.deep_available && "opacity-50 cursor-not-allowed",
              )}
              data-testid="nadia-deep-toggle"
              title={lang === "es" ? "Análisis profundo (cuenta 3x)" : "Deep analysis (counts 3x)"}
            >
              <Zap className="h-3 w-3" />
              {lang === "es" ? "Análisis profundo" : "Deep analysis"}
              {deep && ` · ${usage?.deep_cost || 3}x`}
            </button>
            {usage && (
              <span className="text-[10px] text-blue-200/60">
                {usage.remaining} {lang === "es" ? "restantes" : "left"}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(draft);
                }
              }}
              disabled={streaming || outOfQuota}
              placeholder={lang === "es" ? "Pregúntame sobre este proyecto…" : "Ask me about this project…"}
              rows={1}
              className="min-h-[44px] max-h-[120px] resize-none bg-white/[0.05] border-white/10 text-slate-100 placeholder:text-blue-200/40 focus-visible:ring-blue-400/30"
              data-testid="nadia-input"
            />
            <Button
              type="submit"
              disabled={!draft.trim() || streaming || outOfQuota}
              className="self-end bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white shadow-lg shadow-blue-500/30"
              data-testid="nadia-send"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </aside>
    </>
  );
}

function MessageBubble({ msg, live }) {
  const isUser = msg.role === "user";
  const isAssistant = msg.role === "assistant";
  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")} data-testid={`nadia-msg-${msg.role}`}>
      <div className={cn(
        "max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap break-words",
        isUser
          ? "bg-blue-600 text-white rounded-br-md"
          : "bg-white/[0.06] border border-blue-400/15 rounded-bl-md text-slate-100",
      )}>
        {isAssistant && msg.deep && (
          <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-violet-500 to-blue-500 text-white px-1.5 py-0.5 rounded-full mb-1.5">
            <Zap className="h-3 w-3" /> DEEP
          </div>
        )}
        <FormattedText text={msg.text} />
        {live && <span className="inline-block w-1.5 h-3 ml-0.5 bg-blue-400 animate-pulse rounded-sm" />}
      </div>
    </div>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  // Simple markdown-lite: **bold** and headings **Conclusión**, **Evidencia**, **Acciones sugeridas**.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          const inner = p.slice(2, -2);
          const isHeading = /^(Conclusión|Evidencia|Acciones sugeridas|Conclusion|Evidence|Suggested actions)$/i.test(inner);
          return isHeading
            ? <div key={i} className="font-semibold text-blue-300 mt-2 mb-1 first:mt-0 uppercase tracking-wider text-[11px]">{inner}</div>
            : <span key={i} className="font-semibold">{inner}</span>;
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}
